import React, { useCallback } from 'react';
import { adminAPI } from '../../services/api';
import AsyncTypeahead, { AsyncTypeaheadOption } from '../ui/AsyncTypeahead';

export type CustomerTypeaheadOption = AsyncTypeaheadOption & {
  email: string;
  localCustomerId?: string | null;
  stripeCustomerId?: string | null;
};

type CustomerTypeaheadProps = {
  value: CustomerTypeaheadOption | null;
  onChange: (value: CustomerTypeaheadOption | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
};

function customerLabel(customer: any): string {
  const firstName = customer.firstName || customer.first_name;
  const lastName = customer.lastName || customer.last_name;
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return fullName || customer.email || `Customer ${customer.id}`;
}

export default function CustomerTypeahead({
  value,
  onChange,
  placeholder = 'Search customer by name or email...',
  className,
  disabled,
}: CustomerTypeaheadProps) {
  const loadCustomers = useCallback(async (query: string): Promise<CustomerTypeaheadOption[]> => {
    const response = await adminAPI.getCustomers({
      search: query || undefined,
      limit: 10,
      page: 1,
    });

    const payload = response?.data;
    const customers = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.customers)
      ? payload.customers
      : [];

    return customers
      .filter((customer: any) => customer?.id)
      .map((customer: any) => {
        const label = customerLabel(customer);
        const email = customer.email || 'No email';
        const localCustomerId = customer.local_customer_id || customer.localCustomerId || null;
        const stripeCustomerId = customer.stripe_customer_id || customer.stripeCustomerId || (
          typeof customer.id === 'string' && customer.id.startsWith('cus_') ? customer.id : null
        );
        return {
          id: String(localCustomerId || customer.id),
          label,
          description: email,
          email,
          localCustomerId,
          stripeCustomerId,
        } satisfies CustomerTypeaheadOption;
      });
  }, []);

  return (
    <AsyncTypeahead
      value={value}
      onChange={(option) => onChange(option as CustomerTypeaheadOption | null)}
      loadOptions={loadCustomers}
      placeholder={placeholder}
      emptyMessage="No customers matched your search."
      className={className}
      disabled={disabled}
    />
  );
}
